import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { CONTACT, telHref, telHref2, mailtoHref } from "@/config/contact";
import { useI18n } from "@/i18n/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactForm() {
  const { tr } = useI18n();
  const f = tr.contact.form;
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(f.required_toast);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(f.ok_toast);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(f.err_toast);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 lg:py-32 relative border-t border-white/10" data-testid="contact-section">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-96 w-[80%] rounded-full bg-[color:var(--mc-primary)]/20 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 relative">
        <div className="lg:col-span-5">
          <p className="overline text-[color:var(--mc-gold)]">{tr.contact.overline}</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-semibold tracking-tight mt-4 leading-tight text-white">
            {tr.contact.title_pre}{" "}
            <em className="not-italic italic bg-gradient-to-r from-[color:var(--mc-gold-2)] to-[color:var(--mc-gold)] bg-clip-text text-transparent">
              {tr.contact.title_em}
            </em>
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-white/70">{tr.contact.subtitle}</p>

          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-4 text-white" data-testid="contact-phone">
              <a href={telHref} className="h-12 w-12 rounded-full flex items-center justify-center bg-white/[0.06] border border-white/15 hover:border-[color:var(--mc-cyan)]/50 transition-colors">
                <Phone className="h-5 w-5 text-[color:var(--mc-cyan)]" />
              </a>
              <div>
                <p className="text-xs text-white/55 uppercase tracking-wider">{tr.contact.label_phone}</p>
                <a href={telHref} className="font-heading text-lg block hover:text-[color:var(--mc-cyan)] transition-colors">
                  {CONTACT.phoneDisplay}
                </a>
                <a href={telHref2} className="text-sm text-white/70 hover:text-white block" data-testid="contact-phone-2">
                  {CONTACT.phoneDisplay2}
                </a>
              </div>
            </div>
            <a href={mailtoHref} className="flex items-center gap-4 group text-white" data-testid="contact-email">
              <span className="h-12 w-12 rounded-full flex items-center justify-center bg-white/[0.06] border border-white/15 group-hover:border-[color:var(--mc-cyan)]/50 transition-colors">
                <Mail className="h-5 w-5 text-[color:var(--mc-cyan)]" />
              </span>
              <div>
                <p className="text-xs text-white/55 uppercase tracking-wider">{tr.contact.label_email}</p>
                <p className="font-heading text-lg break-all">{CONTACT.email}</p>
              </div>
            </a>
            <a
              href={CONTACT.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 group text-white"
              data-testid="contact-address"
            >
              <span className="h-12 w-12 rounded-full flex items-center justify-center bg-white/[0.06] border border-white/15 group-hover:border-[color:var(--mc-cyan)]/50 transition-colors">
                <MapPin className="h-5 w-5 text-[color:var(--mc-cyan)]" />
              </span>
              <div>
                <p className="text-xs text-white/55 uppercase tracking-wider">{tr.contact.label_location}</p>
                <p className="font-heading text-lg leading-snug">{CONTACT.addressLine1}</p>
                <p className="text-sm text-white/70">{CONTACT.addressLine2}</p>
              </div>
            </a>
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          onSubmit={onSubmit}
          className="lg:col-span-7 rounded-3xl p-8 lg:p-10 mc-card"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="overline text-[color:var(--mc-cyan)]">{f.name} *</label>
              <input name="name" value={form.name} onChange={onChange} className="mc-input mt-2" placeholder={f.name_ph} required data-testid="contact-input-name" />
            </div>
            <div>
              <label className="overline text-[color:var(--mc-cyan)]">{f.email} *</label>
              <input name="email" type="email" value={form.email} onChange={onChange} className="mc-input mt-2" placeholder={f.email_ph} required data-testid="contact-input-email" />
            </div>
            <div>
              <label className="overline text-[color:var(--mc-cyan)]">{f.phone}</label>
              <input name="phone" value={form.phone} onChange={onChange} className="mc-input mt-2" placeholder={f.phone_ph} data-testid="contact-input-phone" />
            </div>
            <div>
              <label className="overline text-[color:var(--mc-cyan)]">{f.service}</label>
              <select name="service" value={form.service} onChange={onChange} className="mc-input mt-2" data-testid="contact-input-service">
                <option value="">{f.service_ph}</option>
                {f.services.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
            </div>
          </div>
          <div className="mt-5">
            <label className="overline text-[color:var(--mc-cyan)]">{f.message} *</label>
            <textarea name="message" rows={5} value={form.message} onChange={onChange} className="mc-input mt-2 resize-none" placeholder={f.message_ph} required data-testid="contact-input-message" />
          </div>
          <button type="submit" disabled={loading} className="btn-gold mt-7 w-full sm:w-auto justify-center disabled:opacity-70" data-testid="contact-submit">
            {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> {f.submitting}</>) : (<>{f.submit} <Send className="h-4 w-4" /></>)}
          </button>
          <p className="text-xs text-white/55 mt-4">{f.disclaimer}</p>
        </motion.form>
      </div>
    </section>
  );
}
